// ============================================================================
// PERMISSION SERVICE
// ============================================================================
// API calls for role permission tree retrieval and bulk assignment.
// Includes response transformation: the backend returns permission data as
// permissionMap/grantedActions/deniedActions per node, which is transformed
// into the frontend's PermissionActionDto[] format.

import { get, put } from '../client/axiosClient';
import { rolePermissionEndpoints, userPermissionEndpoints, myPermissionsEndpoints } from '../endpoints/iam.endpoints';
import type { ApiResponse, ID } from '../types';
import type { UserNavigationDto } from '../types/iam.types';
import type {
  RolePermissionTreeDto,
  AssignPermissionsRequest,
  UserPermissionTreeDto,
  AssignUserPermissionsRequest,
  UserEffectivePermissionsDto,
  PermissionActionDto,
  ModulePermissionNodeDto,
  MenuPermissionNodeDto,
  ScreenPermissionNodeDto,
  ScreenElementPermissionDto,
  ResourcePermissionNodeDto,
} from '../types/iam.types';

// ============================================================================
// BACKEND → FRONTEND TRANSFORMATION
// ============================================================================
// The backend returns each tree node with:
//   permissionMap: { actionCode → permissionId }
//   grantedActions: string[]
//   deniedActions: string[]
// The frontend expects:
//   actions: PermissionActionDto[] (one entry per action in the catalog)
//
// The top-level response also includes an `actions` catalog (all available
// actions from tbl_action). We use this to build the per-node `actions` array.

interface BackendActionCatalogEntry {
  id: string;
  actionCode: string;
  actionName: string;
  isSystem: boolean;
  sortOrder: number;
}

interface BackendNodePermissions {
  permissionMap?: Record<string, string>;
  grantedActions?: string[];
  deniedActions?: string[];
}

/** Build PermissionActionDto[] from a backend node's permissionMap/grantedActions/deniedActions */
function buildActionsArray(
  node: BackendNodePermissions,
  actionsCatalog: BackendActionCatalogEntry[],
): PermissionActionDto[] {
  const permMap = node.permissionMap || {};
  const granted = new Set(node.grantedActions || []);
  const denied = new Set(node.deniedActions || []);

  return actionsCatalog.map(action => ({
    actionId: action.id,
    actionCode: action.actionCode,
    actionName: action.actionName,
    permissionId: permMap[action.actionCode] || null,
    isAllowed: granted.has(action.actionCode) ? true
             : denied.has(action.actionCode) ? false
             : null,
  }));
}

function transformElement(el: any, catalog: BackendActionCatalogEntry[]): ScreenElementPermissionDto {
  return {
    elementId: el.elementId,
    elementCode: el.elementCode,
    elementName: el.elementName,
    elementType: el.elementType,
    actions: buildActionsArray(el, catalog),
  };
}

function transformScreen(scr: any, catalog: BackendActionCatalogEntry[]): ScreenPermissionNodeDto {
  return {
    screenId: scr.screenId,
    screenCode: scr.screenCode,
    screenName: scr.screenName,
    actions: buildActionsArray(scr, catalog),
    elements: (scr.elements || []).map((el: any) => transformElement(el, catalog)),
  };
}

/** Recursively collect a menu + its child menus into a flat array with parentMenuId set. */
function collectMenus(menu: any, parentMenuId: string | null, catalog: BackendActionCatalogEntry[]): MenuPermissionNodeDto[] {
  const screens = (menu.screens || []).map((s: any) => transformScreen(s, catalog));
  const self: MenuPermissionNodeDto = {
    menuId: menu.menuId,
    parentMenuId,
    menuCode: menu.menuCode,
    menuName: menu.menuName,
    actions: buildActionsArray(menu, catalog),
    screens,
  };
  const result: MenuPermissionNodeDto[] = [self];
  const childMenus = menu.children || [];
  for (const child of childMenus) {
    result.push(...collectMenus(child, String(menu.menuId), catalog));
  }
  return result;
}

function transformModule(mod: any, catalog: BackendActionCatalogEntry[]): ModulePermissionNodeDto {
  const allMenus: MenuPermissionNodeDto[] = [];
  for (const m of (mod.menus || [])) {
    allMenus.push(...collectMenus(m, null, catalog));
  }
  return {
    moduleId: mod.moduleId,
    moduleCode: mod.moduleCode,
    moduleName: mod.moduleName,
    actions: buildActionsArray(mod, catalog),
    menus: allMenus,
  };
}

function transformResources(resources: any[], catalog: BackendActionCatalogEntry[]): ResourcePermissionNodeDto[] {
  return resources.map((res: any) => ({
    resourceId: res.resourceId,
    resourceType: res.resourceType,
    resourceCode: res.resourceCode,
    resourceName: res.resourceName,
    parentResourceId: res.parentResourceId ?? null,
    sortOrder: res.sortOrder ?? 0,
    actions: buildActionsArray(res, catalog),
  }));
}

/** Transform the raw backend response into the frontend RolePermissionTreeDto format */
function transformRoleTree(raw: any): RolePermissionTreeDto {
  const catalog: BackendActionCatalogEntry[] = raw.actions || [];
  return {
    roleId: raw.roleId,
    roleCode: raw.roleCode,
    roleName: raw.roleName,
    modules: (raw.modules || []).map((m: any) => transformModule(m, catalog)),
    resources: raw.resources ? transformResources(raw.resources, catalog) : undefined,
  };
}

/** Transform user permission tree (same node structure as role tree) */
function transformUserTree(raw: any): UserPermissionTreeDto {
  const catalog: BackendActionCatalogEntry[] = raw.actions || [];
  return {
    userId: raw.userId,
    userName: raw.userName,
    modules: (raw.modules || []).map((m: any) => transformModule(m, catalog)),
    resources: raw.resources ? transformResources(raw.resources, catalog) : undefined,
  };
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class PermissionService {
  /** Fetch the full module→menu→screen→element permission tree for a role */
  async getRolePermissionTree(roleId: ID): Promise<ApiResponse<RolePermissionTreeDto>> {
    const response = await get<ApiResponse<any>>(
      rolePermissionEndpoints.getTree(roleId)
    );
    const apiRes = response.data;
    return {
      ...apiRes,
      data: transformRoleTree(apiRes.data),
    };
  }

  /** Bulk-save permission assignments for a role (replaces existing set) */
  async bulkAssign(
    roleId: ID,
    data: AssignPermissionsRequest
  ): Promise<ApiResponse<{ roleId: ID; count: number }>> {
    const response = await put<ApiResponse<{ roleId: ID; count: number }>>(
      rolePermissionEndpoints.bulkAssign(roleId),
      data
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // USER PERMISSION OVERRIDES
  // --------------------------------------------------------------------------

  /** Fetch user-level override tree (same tree structure, shows only user overrides) */
  async getUserPermissionTree(userId: ID): Promise<ApiResponse<UserPermissionTreeDto>> {
    const response = await get<ApiResponse<any>>(
      userPermissionEndpoints.getTree(userId)
    );
    const apiRes = response.data;
    return {
      ...apiRes,
      data: transformUserTree(apiRes.data),
    };
  }

  /** Bulk-save user-level permission overrides */
  async bulkAssignUserPermissions(
    userId: ID,
    data: AssignUserPermissionsRequest
  ): Promise<ApiResponse<{ userId: ID; count: number }>> {
    const response = await put<ApiResponse<{ userId: ID; count: number }>>(
      userPermissionEndpoints.bulkAssign(userId),
      data
    );
    return response.data;
  }

  /** Fetch effective (merged) permissions for a user — all roles + user overrides */
  async getUserEffectivePermissions(userId: ID): Promise<ApiResponse<UserEffectivePermissionsDto>> {
    const response = await get<ApiResponse<UserEffectivePermissionsDto>>(
      userPermissionEndpoints.getEffective(userId)
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // MY NAVIGATION (current user — dynamic sidebar menu)
  // --------------------------------------------------------------------------

  /** Fetch the current user's navigation tree filtered by role permissions */
  async getMyNavigation(): Promise<UserNavigationDto> {
    const response = await get<ApiResponse<UserNavigationDto>>(
      myPermissionsEndpoints.navigation()
    );
    return response.data.data;
  }
}

export const permissionService = new PermissionService();
export default permissionService;
